import React, { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import useAuth from "../hook/useAuth";
import useGroup from "../hook/useGroup";
import useConfirm from "../hook/useConfirm";
import { post_SendLeadershipEmail, post_SendMinistryEmail } from "../services/SendEmail";
import { upload_CommonFile } from "../services/commonFileUpload";
import baseURL from "../config/api";

type Attachment = {
    file: File;
    fileName: string;
    fileUrl?: string;
    isUploading: boolean;
};

type Props = {
    open: boolean;
    onClose: () => void;
    entityType: "Leadership" | "Ministry";
    entityId: number;
    entityName: string;
};

const SendEmail = ({
    open,
    onClose,
    entityType,
    entityId,
    entityName
}: Props) => {
    const token = Cookies.get("token-my-cdir");

    const { auth }: any = useAuth();
    const { selectedGroupId, isSuperAdmin }: any = useGroup();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [subject, setSubject] = useState<string>("");
    const [message, setMessage] = useState<string>("");
    const [replyToEmail, setReplyToEmail] = useState<string>("");

    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const [attachments, setAttachments] = useState<Attachment[]>([]);

    const [characterCount, setCharacterCount] = useState<number>(0);

    useEffect(() => {
        if (selectedGroupId) {
            fetch(
                `${baseURL}group/${selectedGroupId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token?.toString()}`
                    }
                }
            )
                .then((res) => res.json())
                .then((data) => {
                    if (data?.emailAddress) {
                        setReplyToEmail(data?.emailAddress)
                    }
                })
                .catch((error) => {
                    console.log(error);
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGroupId, open])

    useEffect(() => {
        // Strip HTML when calculating character count
        const tempElement = document.createElement("div");
        tempElement.innerHTML = message;

        const plainText =
            tempElement.textContent ||
            tempElement.innerText ||
            "";

        setCharacterCount(plainText.length);
    }, [message]);

    const resetState = () => {
        setSubject("");
        setMessage("");
        setReplyToEmail("");
        setCharacterCount(0);
        setIsProcessing(false);
        setAttachments([]);
    }

    const closeModal = () => {
        onClose();
        resetState();
    }

    const handleAttachmentClick = () => {
        fileInputRef.current?.click();
    }

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;

        if (!files || files.length === 0) {
            return;
        }

        if (!token) {
            return;
        }

        for (const file of Array.from(files)) {
            const attachment: Attachment = {
                file,
                fileName: file.name,
                isUploading: true
            };

            setAttachments((prev) => [
                ...prev,
                attachment
            ]);

            try {
                const formData = new FormData();

                formData.append("ImageFile", file);

                const response = await upload_CommonFile(formData, token);

                const fileUrl = response

                setAttachments((prev) =>
                    prev.map((item) =>
                        item.file === file
                            ? {
                                ...item,
                                fileUrl,
                                isUploading: false
                            }
                            : item
                    )
                );
            } catch (error) {
                console.error(
                    "File upload failed:",
                    error
                );

                // Remove failed upload
                setAttachments((prev) =>
                    prev.filter((item) => item.file !== file)
                );

                setConfirmPopup(true);

                setConfirmData({
                    title: "Upload Failed",
                    summary: `Failed to upload ${file.name}. Please try again.`,
                    confirmBtn: "OK",
                    isCancelBtn: false,

                    isConfirm: (cb: any) => {
                        cb();
                    }
                });
            }
        }

        // Allow selecting the same file again
        event.target.value = "";
    }

    const removeAttachment = (file: File) => {
        if (isProcessing) {
            return;
        }

        setAttachments((prev) =>
            prev.filter((attachment) => attachment.file !== file)
        );
    }

    const handleCancel = () => {
        if (isProcessing) {
            return;
        }

        if (!subject.trim() && !message.trim() && attachments.length === 0) {
            closeModal();
            return;
        }

        setConfirmPopup(true);

        setConfirmData({
            title: "Cancel Email",
            summary: "Are you sure you want to cancel? Your email content and attachments will be lost.",
            confirmBtn: "Yes, Cancel",
            isCancelBtn: true,

            isConfirm: (cb: any) => {
                closeModal();
                cb();
            }
        });
    }

    const sendEmail = async (cb: any) => {
        setIsProcessing(true);

        try {
            // Make sure files have finished uploading
            const uploadingFiles = attachments.filter((attachment) => attachment.isUploading);

            if (uploadingFiles.length > 0) {
                throw new Error("Please wait until all attachments finish uploading.");
            }

            const attachmentUrls = attachments
                .filter((attachment) => !!attachment.fileUrl)
                .map((attachment) => `${baseURL}commonFile/Get?name=${attachment.fileUrl}`);

            if (entityType === "Leadership") {
                await post_SendLeadershipEmail({
                    subject: subject?.trim(),
                    message: message?.trim(),
                    groupId: selectedGroupId,
                    entityId,
                    replyEmails: [replyToEmail.trim()],
                    ccEmails: [],
                    bccEmails: [],
                    attachmentFilePaths: attachmentUrls,
                    isUserType: isSuperAdmin,
                    userId: auth?.id
                }, token!);
            } else if (entityType === "Ministry") {
                await post_SendMinistryEmail({
                    subject: subject?.trim(),
                    message: message?.trim(),
                    groupId: selectedGroupId,
                    entityId,
                    replyEmails: [replyToEmail.trim()],
                    ccEmails: [],
                    bccEmails: [],
                    attachmentFilePaths: attachmentUrls,
                    isUserType: isSuperAdmin,
                    userId: auth?.id
                }, token!);
            }

            closeModal();
            cb();
        } catch (error: any) {
            console.error(
                "Error sending email:",
                error
            );

            setIsProcessing(false);

            // Close current confirmation
            cb();

            setTimeout(() => {
                setConfirmPopup(true);

                setConfirmData({
                    title: "Email Failed",
                    summary:
                        error?.response?.data?.message ||
                        error?.message ||
                        "Failed to send email. Please try again.",
                    confirmBtn: "OK",
                    isCancelBtn: false,

                    isConfirm: (confirmCb: any) => {
                        confirmCb();
                    }
                });
            }, 100);
        }
    }

    const getPlainText = (html: string): string => {
        const element = document.createElement("div");
        element.innerHTML = html;

        return (element.textContent || element.innerText || "")
            .replace(/\u00a0/g, " ")
            .trim();
    }

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!subject.trim() || !message.trim() || !replyToEmail.trim() || isProcessing) {
            return;
        }

        if (!getPlainText(message)) {
            setConfirmPopup(true);
            setConfirmData({
                title: "Message Required",
                summary: "Please enter a message before sending the email.",
                confirmBtn: "OK",
                isCancelBtn: false,
                isConfirm: (cb: any) => cb(),
            });
            return;
        }

        if (attachments.some((attachment) => attachment.isUploading)) {
            setConfirmPopup(true);

            setConfirmData({
                title: "Uploading Attachment",
                summary: "Please wait until all attachments finish uploading.",
                confirmBtn: "OK",
                isCancelBtn: false,

                isConfirm: (cb: any) => {
                    cb();
                }
            });

            return;
        }

        setConfirmPopup(true);

        setConfirmData({
            title: "Send Email",
            summary: `Are you sure you want to send this email to ${entityName}?`,
            confirmBtn: "Send",
            isCancelBtn: true,
            isConfirm: async (cb: any) => {
                await sendEmail(cb);
            }
        });
    }

    const quillModules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            ["blockquote"],
            ["link"],
            ["clean"]
        ]
    };

    const quillRef = useRef<ReactQuill | null>(null);

    const mergeTags =
        entityType === "Leadership"
            ? [
                { label: "First Name", value: "{{FirstName}}" },
                { label: "Last Name", value: "{{LastName}}" },
                { label: "Full Name", value: "{{FullName}}" },
                { label: "Email", value: "{{Email}}" },
                { label: "Phone Number", value: "{{PhoneNumber}}" },
                { label: "Designation", value: "{{Designation}}" }
            ]
            : [
                { label: "First Name", value: "{{FirstName}}" },
                { label: "Last Name", value: "{{LastName}}" },
                { label: "Full Name", value: "{{FullName}}" },
                { label: "Email", value: "{{Email}}" },
                { label: "Phone Number", value: "{{PhoneNumber}}" },
                { label: "Birthday", value: "{{Birthday}}" },
                { label: "Username", value: "{{UserName}}" }
            ];

    const insertMergeTag = (tag: string) => {
        const editor = quillRef.current?.getEditor();

        if (!editor) return;

        const range = editor.getSelection(true);
        const position = range?.index ?? editor.getLength() - 1;

        editor.insertText(position, tag, "user");
        editor.setSelection(position + tag.length, 0, "user");
        editor.focus();
    }

    const quillFormats = [
        "header",
        "bold",
        "italic",
        "underline",
        "strike",
        "color",
        "background",
        "list",
        "bullet",
        "align",
        "blockquote",
        "link"
    ];


    return (
        <div className={`fixed inset-0 z-[1000] ${open ? "flex" : "hidden"} items-start justify-center bg-slate-950/60 p-3 sm:p-6 backdrop-blur-sm`}>
            <form
                onSubmit={handleSubmit}
                className="relative flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            >
                <div className="flex items-start justify-between border-b border-slate-100 bg-white px-5 py-5 sm:px-7">
                    <div className="flex min-w-0 items-start gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                className="h-6 w-6"
                            >
                                <rect x="3" y="5" width="18" height="14" rx="2" />
                                <path d="m3 7 9 6 9-6" />
                            </svg>
                        </div>

                        <div className="min-w-0">
                            <h2 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xfvl">
                                Compose Email
                            </h2>
                            <p className="mt-1 text-sm text-slate-500">
                                Send a message to{" "}
                                <span className="font-semibold text-slate-700">
                                    {entityName}
                                </span>
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isProcessing}
                        aria-label="Close email form"
                        className="ml-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className="h-5 w-5"
                        >
                            <path d="m18 6-12 12M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-7">
                    <div className="flex items-start gap-3 rounded-xl border border-secondary/20 bg-secondary/5 p-4">
                        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-secondary shadow-sm">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                className="h-5 w-5"
                            >
                                <circle cx="12" cy="8" r="4" />
                                <path d="M4 21a8 8 0 0 1 16 0" />
                            </svg>
                        </div>

                        <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold uppercase tracking-wider text-secondary">
                                Recipient
                            </p>
                            <p className="mt-1 break-words text-sm font-semibold text-slate-900">
                                {entityName || "Not provided"}
                            </p>
                        </div>

                        <span className="shrink-0 rounded-full border border-secondary/20 bg-white px-2.5 py-1 text-xs font-medium text-secondary">
                            Email
                        </span>
                    </div>

                    <div>
                        <label
                            htmlFor="emailSubject"
                            className="mb-2 block text-sm font-semibold text-slate-700"
                        >
                            Subject <span className="text-red-500">*</span>
                        </label>

                        <input
                            id="emailSubject"
                            name="emailSubject"
                            type="text"
                            required
                            maxLength={200}
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Enter a clear subject..."
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="replyToEmail"
                            className="mb-2 block text-sm font-semibold text-slate-700"
                        >
                            Reply-To Email <span className="text-red-500">*</span>
                        </label>

                        <input
                            id="replyToEmail"
                            name="replyToEmail"
                            type="email"
                            required
                            value={replyToEmail}
                            onChange={(e) => setReplyToEmail(e.target.value)}
                            placeholder="name@example.com"
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                        />

                        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                className="h-3.5 w-3.5"
                            >
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 11v5m0-8h.01" />
                            </svg>
                            Replies to this email will be sent to this address.
                        </p>
                    </div>

                    <div>
                        <div className="mb-2 flex items-center justify-between gap-3">
                            <label className="text-sm font-semibold text-slate-700">
                                Message <span className="text-red-500">*</span>
                            </label>

                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">
                                {characterCount} characters
                            </span>
                        </div>

                        <div className="mb-3 overflow-hidden rounded-xl border border-secondary/20 bg-secondary/5">
                            <div className="flex items-start gap-3 border-b border-secondary/15 px-4 py-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-secondary shadow-sm">
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        className="h-5 w-5"
                                    >
                                        <path d="M8 8 4 12l4 4M16 8l4 4-4 4M14 5l-4 14" />
                                    </svg>
                                </div>

                                <div>
                                    <p className="text-sm font-semibold text-slate-800">
                                        Personalize your email
                                    </p>
                                    <p className="mt-0.5 text-xs leading-5 text-slate-500">
                                        Insert personalized fields into your message.
                                    </p>
                                </div>
                            </div>

                            <div className="p-3">
                                <div className="mb-3 flex items-center justify-between gap-2">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Available fields
                                    </span>

                                    <span className="rounded-full bg-secondary/10 px-2.5 py-1 text-[10px] font-semibold text-secondary">
                                        {entityType}
                                    </span>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {
                                        mergeTags?.map((tag) => (
                                            <button
                                                key={tag.value}
                                                type="button"
                                                title={`Insert ${tag.value}`}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => insertMergeTag(tag.value)}
                                                className="inline-flex items-center gap-1.5 rounded-lg border border-secondary/20 bg-white px-3 py-2 text-xs font-medium text-secondary transition hover:border-secondary hover:bg-secondary hover:text-white active:scale-[0.98]"
                                            >
                                                <span className="opacity-70">{"{{"}</span>
                                                {tag.label}
                                                <span className="opacity-70">{"}}"}</span>
                                            </button>
                                        ))
                                    }
                                </div>

                                <p className="mt-3 text-[11px] leading-4 text-slate-500">
                                    Example: Hello {"{{FirstName}}"}, your message is ready.
                                </p>
                            </div>
                        </div>

                        <div className="email-editor overflow-hidden rounded-xl">
                            <ReactQuill
                                ref={quillRef}
                                theme="snow"
                                value={message}
                                onChange={setMessage}
                                modules={quillModules}
                                formats={quillFormats}
                                placeholder="Write your email message here..."
                            />
                        </div>
                    </div>

                    <div>
                        <div className="mb-2 flex items-center justify-between gap-3">
                            <label className="text-sm font-semibold text-slate-700">
                                Attachments
                            </label>

                            <span className="text-xs text-slate-500">
                                {attachments.length}{" "}
                                {attachments.length === 1 ? "file" : "files"}
                            </span>
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            className="hidden"
                            onChange={handleFileChange}
                        />

                        <button
                            type="button"
                            disabled={isProcessing}
                            onClick={handleAttachmentClick}
                            className="group flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/70 px-4 py-6 text-center transition hover:border-blue-400 hover:bg-blue-50/50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm ring-1 ring-slate-200 transition group-hover:ring-blue-200">
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.7"
                                    className="h-6 w-6"
                                >
                                    <path d="M12 16V4m-4 4 4-4 4 4" />
                                    <path d="M20 16.5v2A1.5 1.5 0 0 1 18.5 20h-13A1.5 1.5 0 0 1 4 18.5v-2" />
                                </svg>
                            </div>

                            <span className="text-sm font-semibold text-slate-700">
                                Click to attach files
                            </span>
                            <span className="mt-1 text-xs text-slate-500">
                                Select one or more files from your device
                            </span>
                        </button>

                        {
                            attachments.length > 0 && (
                                <div className="mt-3 space-y-2">
                                    {
                                        attachments.map((attachment) => (
                                            <div
                                                key={`${attachment.file.name}-${attachment.file.lastModified}`}
                                                className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3 transition hover:border-slate-300"
                                            >
                                                <div className="flex min-w-0 items-center gap-3">
                                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                                                        <svg
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="1.7"
                                                            className="h-5 w-5"
                                                        >
                                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                            <path d="M14 2v6h6M8 13h8m-8 4h8" />
                                                        </svg>
                                                    </div>

                                                    <div className="min-w-0">
                                                        <p className="truncate text-sm font-medium text-slate-800">
                                                            {attachment.fileName}
                                                        </p>
                                                        <p className="mt-0.5 text-xs text-slate-500">
                                                            {(attachment.file.size / 1024).toFixed(1)} KB
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex shrink-0 items-center gap-3">
                                                    <span
                                                        className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium ${attachment.isUploading
                                                            ? "bg-amber-50 text-amber-700"
                                                            : "bg-emerald-50 text-emerald-700"
                                                            }`}
                                                    >
                                                        <span
                                                            className={`h-1.5 w-1.5 rounded-full ${attachment.isUploading
                                                                ? "animate-pulse bg-amber-500"
                                                                : "bg-emerald-500"
                                                                }`}
                                                        />
                                                        {attachment.isUploading
                                                            ? "Uploading"
                                                            : "Uploaded"}
                                                    </span>

                                                    <button
                                                        type="button"
                                                        disabled={
                                                            isProcessing ||
                                                            attachment.isUploading
                                                        }
                                                        onClick={() =>
                                                            removeAttachment(attachment.file)
                                                        }
                                                        className="rounded-lg px-2 py-1.5 text-xs font-semibold text-red-500 transition hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            )
                        }
                    </div>
                </div>

                <div className="border-t border-slate-200 bg-slate-50/80 px-5 py-4 sm:px-7">
                    <div className="mb-4 flex items-center justify-between gap-3 text-xs text-slate-500">
                        <span className="flex min-w-0 items-center gap-2">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                className="h-4 w-4 shrink-0"
                            >
                                <rect x="4" y="10" width="16" height="11" rx="2" />
                                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                            </svg>
                            <span className="truncate">
                                Review your message before sending.
                            </span>
                        </span>

                        <span className="shrink-0 font-medium text-slate-600">
                            {attachments.length} attached
                        </span>
                    </div>

                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                        <button
                            type="button"
                            disabled={isProcessing}
                            onClick={handleCancel}
                            className="inline-flex min-h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-32"
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            disabled={
                                isProcessing ||
                                !subject.trim() ||
                                !message.trim() ||
                                !replyToEmail.trim() ||
                                attachments.some((attachment) => attachment.isUploading)
                            }
                            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-secondary px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-custom-blue hover:shadow disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-40"
                        >
                            {
                                isProcessing
                                    ? (
                                        <>
                                            <svg
                                                className="h-4 w-4 animate-spin"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                            >
                                                <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="9"
                                                    stroke="currentColor"
                                                    strokeWidth="3"
                                                    opacity=".25"
                                                />
                                                <path
                                                    d="M21 12a9 9 0 0 0-9-9"
                                                    stroke="currentColor"
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <svg
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.8"
                                                className="h-4 w-4"
                                            >
                                                <path d="m22 2-7 20-4-9-9-4Z" />
                                                <path d="M22 2 11 13" />
                                            </svg>
                                            Send Email
                                        </>
                                    )
                            }
                        </button>
                    </div>
                </div>
            </form>

            <style>
                {`
                .email-editor .ql-toolbar {
                    border: 1px solid #e2e8f0;
                    border-radius: 12px 12px 0 0;
                    background: #f8fafc;
                    padding: 10px;
                }

                .email-editor .ql-container {
                    min-height: 180px;
                    border: 1px solid #e2e8f0;
                    border-top: 0;
                    border-radius: 0 0 12px 12px;
                    font-family: inherit;
                    font-size: 14px;
                }

                .email-editor .ql-editor {
                    min-height: 180px;
                    padding: 16px;
                    color: #334155;
                    line-height: 1.7;
                }

                .email-editor .ql-editor.ql-blank::before {
                    color: #94a3b8;
                    font-style: normal;
                    left: 16px;
                    right: 16px;
                }

                .email-editor .ql-snow .ql-stroke {
                    stroke: #64748b;
                }

                .email-editor .ql-snow .ql-fill {
                    fill: #64748b;
                }

                .email-editor .ql-toolbar button:hover .ql-stroke {
                    stroke: #2563eb;
                }

                .email-editor .ql-toolbar button:hover .ql-fill {
                    fill: #2563eb;
                }

                .email-editor .ql-toolbar .ql-picker-label:hover {
                    color: #2563eb;
                }
            `}
            </style>
        </div>
    );
};

export default SendEmail;
